import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Rocket, 
  Users, 
  Trophy, 
  Heart, 
  TrendingUp,
  Upload,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const Careers = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    areaOfInterest: '',
    message: '',
  });
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const whyJoinReasons = [
    {
      icon: Rocket,
      title: "Fast-Growing Startup",
      description: "Work on a fast-growing entertainment tech startup"
    },
    {
      icon: Users,
      title: "Impact Millions",
      description: "Help bring skill-based gaming to every Indian"
    },
    {
      icon: Trophy,
      title: "Real Winners",
      description: "Create real winners with real rewards"
    },
    {
      icon: Heart,
      title: "Great Culture",
      description: "Fun, flexible & passionate culture"
    },
    {
      icon: TrendingUp,
      title: "Growth Opportunities",
      description: "Huge growth opportunities"
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Please upload a PDF file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setResume(file);
      toast.success('Resume uploaded successfully');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName || !formData.email || !formData.phone || !formData.areaOfInterest) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!resume) {
      toast.error('Please upload your resume');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('areaOfInterest', formData.areaOfInterest);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('resume', resume);

      const response = await fetch(`${window.location.origin}/api/careers/submit`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Application submitted successfully!');
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          city: '',
          areaOfInterest: '',
          message: ''
        });
        setResume(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting career form:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('career-form').scrollIntoView({ behavior: 'smooth' });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black pt-24 pb-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="animate-fade-in-up">
            <CheckCircle className="w-24 h-24 text-yellow-400 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-white mb-4">Thanks for connecting!</h1>
            <p className="text-xl text-gray-300 mb-8">
              We'll reach out when a relevant opportunity becomes available.
            </p>
            <p className="text-lg text-yellow-400 mb-8">
              Meanwhile, keep playing & keep winning on FokatMain!
            </p>
            <div className="flex gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/play-games'}
                className="gaming-button"
              >
                Play Games
              </Button>
              <Button 
                onClick={() => setSubmitted(false)}
                className="gaming-button-secondary"
              >
                Submit Another Application
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 mb-6 px-4 py-2 text-lg">
            Join Our Team
          </Badge>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in-up">
            Build the Future of
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-blue-400"> Gaming</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            We are not hiring for specific roles right now, but we would love to connect with talented people who believe in our mission to build India's most exciting skill-based gaming platform.
          </p>
          <Button 
            onClick={scrollToForm}
            className="gaming-button text-lg px-8 py-6 animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            Register Your Interest
          </Button>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-900/10 to-purple-900/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Why Join <span className="text-yellow-400">FokatMain</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyJoinReasons.map((reason, index) => (
              <Card 
                key={index}
                className="gaming-card hover:scale-105 transition-transform duration-300 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-blue-400 rounded-lg flex items-center justify-center mb-4">
                    <reason.icon className="w-8 h-8 text-black" />
                  </div>
                  <CardTitle className="text-xl text-white">{reason.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400">{reason.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Career Form Section */}
      <section id="career-form" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <Card className="gaming-card">
            <CardHeader>
              <div className="text-center mb-6">
                <Briefcase className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <CardTitle className="text-3xl text-white mb-2">Register Your Interest</CardTitle>
                <p className="text-gray-400">Share your details and we'll connect when there's a match</p>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Full Name <span className="text-red-400">*</span>
                  </label>
                  <Input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="bg-black/30 border-gray-700 text-white"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      className="bg-black/30 border-gray-700 text-white pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Phone Number <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      className="bg-black/30 border-gray-700 text-white pl-10"
                      required
                    />
                  </div>
                </div>

                {/* City */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    City <span className="text-gray-500">(Optional)</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Your city"
                      className="bg-black/30 border-gray-700 text-white pl-10"
                    />
                  </div>
                </div>

                {/* Area of Interest */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Area of Interest <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="areaOfInterest"
                    value={formData.areaOfInterest}
                    onChange={handleInputChange}
                    className="w-full bg-black/30 border border-gray-700 text-white rounded-lg px-4 py-3"
                    required
                  >
                    <option value="">Select an area</option>
                    <option value="Tech">Tech</option>
                    <option value="Design">Design</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Content">Content</option>
                    <option value="Community/Operations">Community/Operations</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Resume Upload */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Upload Resume <span className="text-red-400">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-yellow-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      id="resume-upload"
                    />
                    <label htmlFor="resume-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      {resume ? (
                        <p className="text-yellow-400 font-semibold">{resume.name}</p>
                      ) : (
                        <>
                          <p className="text-gray-300 mb-1">Click to upload or drag and drop</p>
                          <p className="text-sm text-gray-500">PDF only, max 5MB</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-gray-300 mb-2 font-semibold">
                    Why do you want to join? <span className="text-gray-500">(Optional)</span>
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about yourself and why you want to join FokatMain..."
                    className="bg-black/30 border-gray-700 text-white min-h-32"
                    rows={4}
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="gaming-button w-full text-lg py-6"
                >
                  {loading ? 'Submitting...' : 'Join the Talent List'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Careers;
